/**
 * Interceptor global de negações RLS.
 *
 * Substitui `supabase.from(table)` por um Proxy que detecta, ao final da
 * chain (`.then`), respostas com `error` de RLS em tabelas sensíveis e
 * envia para `log_rls_denial` (best-effort). Assim, todos os hooks/consumidores
 * existentes ficam auditados sem precisar alterar cada chamada.
 *
 * Decisões:
 * - Só intercepta tabelas em SENSITIVE_TABLES (do logger) — zero overhead nas demais.
 * - Detecta a operação a partir do método chamado (select/insert/update/delete/upsert).
 * - Não altera o resultado retornado ao chamador.
 * - Idempotente: marca a instância para não envolver duas vezes.
 */
import { supabase } from "@/integrations/supabase/client";
import { logRlsDenial, type RlsOperation } from "./rlsDenialLogger";

// Mantenha em sincronia com rlsDenialLogger.ts
const SENSITIVE_TABLES = new Set<string>([
  "profiles",
  "venues",
  "user_locations",
  "matches",
  "messages",
  "likes",
  "user_keys",
  "user_subscriptions",
  "user_coins",
  "coin_transactions",
  "mfa_recovery_codes",
  "age_verifications",
  "venue_kyc_attempts",
  "admin_audit_logs",
  "rls_denial_log",
  "fiscal_receipts",
  "blacklist_minors",
  "consent_logs",
  "terms_acceptances",
  "user_audit_log",
  "suspicious_activity",
]);

const OP_BY_METHOD: Record<string, RlsOperation> = {
  select: "select",
  insert: "insert",
  update: "update",
  delete: "delete",
  upsert: "insert",
};

type AnyBuilder = Record<string | symbol, unknown> & {
  then?: (onFulfilled?: unknown, onRejected?: unknown) => Promise<unknown>;
};

function wrapBuilder(builder: AnyBuilder, table: string, op: RlsOperation): AnyBuilder {
  return new Proxy(builder, {
    get(target, prop, receiver) {
      const value = Reflect.get(target, prop, receiver);

      if (prop === "then" && typeof value === "function") {
        const origThen = value as (
          onFulfilled?: (res: { error?: unknown }) => unknown,
          onRejected?: unknown,
        ) => Promise<unknown>;
        return (onFulfilled?: (res: { error?: unknown }) => unknown, onRejected?: unknown) =>
          origThen.call(
            target,
            (res: { error?: unknown }) => {
              if (res && typeof res === "object" && res.error) {
                void logRlsDenial(table, op, res.error);
              }
              return onFulfilled ? onFulfilled(res) : res;
            },
            onRejected,
          );
      }

      if (typeof value !== "function") return value;

      return (...args: unknown[]) => {
        const result = (value as (...a: unknown[]) => unknown).apply(target, args);
        const methodName = typeof prop === "string" ? prop : "";
        const nextOp: RlsOperation = OP_BY_METHOD[methodName] ?? op;

        if (
          result &&
          typeof result === "object" &&
          (typeof (result as AnyBuilder).then === "function" ||
            // chain methods (eq, in, order, limit, range, ...) retornam builder
            methodName in OP_BY_METHOD ||
            ["eq", "neq", "gt", "gte", "lt", "lte", "like", "ilike", "in", "is", "or",
             "not", "filter", "match", "contains", "containedBy", "rangeGt", "rangeLt",
             "rangeGte", "rangeLte", "rangeAdjacent", "overlaps", "textSearch",
             "order", "limit", "range", "single", "maybeSingle", "csv", "abortSignal",
             "throwOnError", "returns", "select"].includes(methodName))
        ) {
          return wrapBuilder(result as AnyBuilder, table, nextOp);
        }
        return result;
      };
    },
  });
}

const INSTALLED = Symbol.for("zappys.rlsInterceptor.installed");

// RPCs sensíveis cujo erro também deve ser auditado. Use prefixos para cobrir famílias.
const SENSITIVE_RPC_EXACT = new Set<string>([
  "log_rls_denial",
  "get_safe_dates_admin",
  "start_boost",
  "issue_receipt",
  "check_and_consume_rate_limit",
]);
const SENSITIVE_RPC_PREFIX = ["admin_", "security_"];

function isSensitiveRpc(name: string): boolean {
  if (SENSITIVE_RPC_EXACT.has(name)) return true;
  return SENSITIVE_RPC_PREFIX.some((p) => name.startsWith(p));
}

export function installRlsInterceptor(): void {
  const client = supabase as unknown as Record<string | symbol, unknown> & {
    from: (table: string) => unknown;
    rpc: (name: string, args?: unknown, opts?: unknown) => AnyBuilder;
    channel: (name: string, opts?: unknown) => {
      on: (...a: unknown[]) => unknown;
      subscribe: (cb?: (status: string, err?: unknown) => void) => unknown;
    };
  };
  if (client[INSTALLED]) return;

  // -------- from() --------
  const origFrom = client.from.bind(supabase);
  client.from = (table: string) => {
    const builder = origFrom(table) as AnyBuilder;
    if (!SENSITIVE_TABLES.has(table)) return builder;
    return wrapBuilder(builder, table, "select");
  };

  // -------- rpc() --------
  const origRpc = client.rpc.bind(supabase);
  client.rpc = (name: string, args?: unknown, opts?: unknown) => {
    const builder = origRpc(name, args, opts);
    if (!isSensitiveRpc(name)) return builder;
    // Reusa o wrapBuilder marcando como op "rpc" e table = nome da função.
    return wrapBuilder(builder as AnyBuilder, `rpc:${name}`, "rpc");
  };

  // -------- channel().subscribe() --------
  const origChannel = client.channel.bind(supabase);
  client.channel = (name: string, opts?: unknown) => {
    const ch = origChannel(name, opts);
    const origSubscribe = ch.subscribe.bind(ch);
    ch.subscribe = (cb?: (status: string, err?: unknown) => void) =>
      origSubscribe((status: string, err?: unknown) => {
        if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
          // Best-effort: registra como realtime no canal (table = nome do canal).
          void logRlsDenial(
            `realtime:${name}`,
            "realtime",
            err ?? { code: status, message: status },
          );
        }
        cb?.(status, err);
      });
    return ch;
  };

  client[INSTALLED] = true;
}
